/**
 * VistaSegmentadaDiaNoche — Armar Pedido con toggle DÍA / NOCHE.
 *
 * Solo se renderiza cuando la operación destino tiene
 * `flagPedidoSegmentado = true`. Mientras el flag esté apagado
 * (estado actual para las 107 operaciones de QA), el componente NO
 * aparece y `ArmarPedido` sigue mostrándose como hoy.
 *
 * Decisiones (acordadas con Jorge 2026-06-18):
 *   - Día:   COLACIÓN PRINCIPAL, DIETA BLANDA, ALMUERZO SIN GLUTEN,
 *            HIPOCALÓRICO, COLACIÓN FRÍA (las dos últimas son `dual`).
 *   - Noche: CENA, HIPOCALÓRICO, COLACIÓN FRÍA.
 *   - Por-defectos (ensalada, sopa, postre, pan, cubiertos, agua,
 *     empaque) NO van en la grilla elegible. Se muestran en sección
 *     aparte "Se incluyen automáticamente" no editable, con la
 *     cantidad recalculada en vivo.
 *   - Plato compuesto (fondo + guarnición) se sigue agrupando como
 *     en la vista carta.
 *
 * El segmento `transversal` (BEBIDA, DESAYUNO) NO se ve en esta vista
 * — se mantiene fuera del flujo día/noche por ahora.
 */

import React, { useMemo, useState } from 'react';
import {
  Sun,
  Moon,
  Sparkles,
  Save,
  AlertCircle,
  GlassWater,
} from 'lucide-react';
import type {
  EstructuraArmarPedido,
  PorDefectoPedido,
} from '../../types/pedidosNuevos';

type DiaEstructura = EstructuraArmarPedido['dias'][number];

type Segmento = 'dia' | 'noche';

/** Segmento sintético usado para las cantidades de BEBIDA. Las bebidas son
 *  transversales (aplican al día completo, no a día vs noche), así que se
 *  guardan bajo un tercer segmento — `transversal` — coherente con el
 *  contrato del backend en pedido_detalles.segmento. */
const SEG_BEBIDA = 'transversal';

interface Props {
  estructura: EstructuraArmarPedido;
  diaActivo: string;
  /** key = `${tipoPlatoId}-${filaOrden}-${segmento}` (con flag activo) */
  cantidadesDia: Map<string, number>;
  /** Notifica al padre cambios por celda + segmento. */
  onCantidadChange: (
    fecha: string,
    tipoPlatoId: number,
    filaOrden: number,
    segmento: Segmento,
    nuevaCantidad: number
  ) => void;
  /** Llamado al confirmar guardar. El padre arma el payload con segmento. */
  onGuardar: (fecha: string) => void;
  guardando: boolean;
  tieneCambios: boolean;
  /** Fase 6-cierre (2026-07-01): abre el modal para pasar el pedido de
   *  borrador → solicitado. Espejo del que usa la vista carta unificada. */
  onConfirmar?: (fecha: string, nombreDia: string, pedidoId: number) => void;
  /** Estado y pedidoId del día actual — se usan para gatear el botón
   *  Confirmar (solo activo si hay pedidoId y no hay cambios sin guardar). */
  estadoDiaActivo?: string;
  pedidoIdDiaActivo?: number | null;
  nombreDiaActivo?: string;
  /** ID del tipo_plato BEBIDA (11 en el dominio). Pasado desde el padre para
   *  no duplicar la constante. Cuando la minuta tiene filas BEBIDA, se
   *  renderiza una sección "🥤 Bebidas" independiente de los tabs día/noche
   *  (las bebidas son transversales al día). */
  bebidaTipoPlatoId?: number;
  /** Fase 6 fix H-03 (2026-07-01): navegación entre días. Antes la vista
   *  segmentada no tenía cómo cambiar de día — solo la unificada
   *  (CartaDeMenu.setDiaActivo). Ahora recibe la lista completa de días
   *  y notifica al padre cuando el usuario selecciona otro. Sin estos
   *  props, la barra no se renderiza (compat). */
  dias?: DiaEstructura[];
  onCambiarDia?: (fecha: string) => void;
  /** Fase 6 fix H-05 (2026-07-01): total de porciones del día activo
   *  (suma día + noche + transversal). Se usa para gatear el botón
   *  Confirmar — no dejamos confirmar un pedido vacío. Cuando falta,
   *  el gate se vuelve permisivo (compat con integraciones viejas). */
  totalDelDia?: number;
}

const formatearFechaLarga = (iso: string): string => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('es-CL', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    });
  } catch {
    return iso;
  }
};

const claveCantidadSeg = (
  tipoPlatoId: number,
  filaOrden: number,
  segmento: Segmento | 'transversal'
): string => `${tipoPlatoId}-${filaOrden}-${segmento}`;

const VistaSegmentadaDiaNoche: React.FC<Props> = ({
  estructura,
  diaActivo,
  cantidadesDia,
  onCantidadChange,
  onGuardar,
  guardando,
  tieneCambios,
  onConfirmar,
  estadoDiaActivo,
  pedidoIdDiaActivo,
  nombreDiaActivo,
  bebidaTipoPlatoId,
  dias,
  onCambiarDia,
  totalDelDia,
}) => {
  const [segmento, setSegmento] = useState<Segmento>('dia');

  // Filas de la matriz que corresponden al segmento activo. Una fila
  // entra si su tipoPlato.segmento === segmento, o si es `dual` y el
  // segmento es día/noche. Las BEBIDAS se muestran aparte (sección
  // transversal), no en los tabs día/noche.
  const filasDelSegmento = useMemo(() => {
    return estructura.matriz.filter((fila) => {
      const tp = fila.tipoPlato;
      if (bebidaTipoPlatoId !== undefined && tp.id === bebidaTipoPlatoId) {
        return false; // bebidas van en sección aparte
      }
      if (tp.segmento === segmento) return true;
      if (tp.dual && (segmento === 'dia' || segmento === 'noche')) return true;
      return false;
    });
  }, [estructura.matriz, segmento, bebidaTipoPlatoId]);

  // Filas BEBIDA del día actual — transversales al segmento. Solo con celda
  // (receta asignada) para no mostrar huecos vacíos.
  const filasBebidaDia = useMemo(() => {
    if (bebidaTipoPlatoId === undefined) return [];
    return estructura.matriz
      .filter((f) => f.tipoPlato.id === bebidaTipoPlatoId)
      .map((f) => ({ fila: f, celda: f.dias.find((d) => d.fecha === diaActivo) }))
      .filter((r) => !!r.celda?.receta);
  }, [estructura.matriz, bebidaTipoPlatoId, diaActivo]);

  // Total bebidas del día completo (suma de todas las filas BEBIDA).
  const totalBebidasDia = useMemo(() => {
    let t = 0;
    for (const { fila } of filasBebidaDia) {
      const k = claveCantidadSeg(
        fila.tipoPlato.id,
        fila.tipoPlato.filaOrden,
        SEG_BEBIDA
      );
      t += cantidadesDia.get(k) || 0;
    }
    return t;
  }, [filasBebidaDia, cantidadesDia]);

  // Total comida del día completo (día + noche, TODO menos bebidas).
  const totalComidaDia = useMemo(() => {
    let t = 0;
    for (const fila of estructura.matriz) {
      if (
        bebidaTipoPlatoId !== undefined &&
        fila.tipoPlato.id === bebidaTipoPlatoId
      )
        continue;
      const celda = fila.dias.find((d) => d.fecha === diaActivo);
      if (!celda?.receta) continue;
      const kDia = claveCantidadSeg(fila.tipoPlato.id, fila.tipoPlato.filaOrden, 'dia');
      const kNoche = claveCantidadSeg(fila.tipoPlato.id, fila.tipoPlato.filaOrden, 'noche');
      t += (cantidadesDia.get(kDia) || 0) + (cantidadesDia.get(kNoche) || 0);
    }
    return t;
  }, [estructura.matriz, cantidadesDia, diaActivo, bebidaTipoPlatoId]);

  const excedeBebidas = totalBebidasDia > totalComidaDia;

  // Cantidades del segmento activo agrupadas por tipo_plato_id.
  // Necesario para que cada por-defecto pueda restar SOLO sus tipos
  // excluidos (cambio B, 2026-06-22). Espejo exacto de lo que hace el
  // backend en sembrarPorDefecto — backend y UI nunca deben divergir.
  const cantidadesPorTipoEnSegmento = useMemo<Map<number, number>>(() => {
    const m = new Map<number, number>();
    for (const fila of filasDelSegmento) {
      const tipoId = fila.tipoPlato.id;
      const celda = fila.dias.find((d) => d.fecha === diaActivo);
      if (!celda || !celda.receta) continue;
      const k = claveCantidadSeg(tipoId, fila.tipoPlato.filaOrden, segmento);
      const c = cantidadesDia.get(k) || 0;
      m.set(tipoId, (m.get(tipoId) ?? 0) + c);
    }
    return m;
  }, [filasDelSegmento, cantidadesDia, diaActivo, segmento]);

  // Suma del segmento (para el "Total del segmento" del footer y como
  // fallback cuando un por-defecto no tiene exclusiones).
  const totalDelSegmento = useMemo(() => {
    let total = 0;
    for (const v of cantidadesPorTipoEnSegmento.values()) total += v;
    return total;
  }, [cantidadesPorTipoEnSegmento]);

  // Por-defectos del segmento + transversales. Cada uno calcula SU
  // base individual restando los tipos excluidos. Mismo criterio que
  // el backend.
  const porDefectoSegmento = useMemo<
    Array<PorDefectoPedido & { cantidadCalculada: number }>
  >(() => {
    const lista = estructura.porDefecto ?? [];
    const sumaExcluyendo = (excluidos: number[]): number => {
      if (excluidos.length === 0) return totalDelSegmento;
      const setExcl = new Set(excluidos);
      let total = 0;
      for (const [tipoId, c] of cantidadesPorTipoEnSegmento.entries()) {
        if (setExcl.has(tipoId)) continue;
        total += c;
      }
      return total;
    };
    return lista
      .filter((pd) => pd.segmento === segmento || pd.segmento === 'transversal')
      .map((pd) => ({
        ...pd,
        cantidadCalculada: Math.ceil(pd.factor * sumaExcluyendo(pd.excluyeTipos ?? [])),
      }))
      .filter((pd) => pd.cantidadCalculada > 0 || totalDelSegmento === 0);
  }, [estructura.porDefecto, segmento, totalDelSegmento, cantidadesPorTipoEnSegmento]);

  const swatch =
    segmento === 'dia'
      ? {
          bg: 'bg-amber-50 dark:bg-amber-900/10',
          ring: 'ring-amber-400',
          chip: 'bg-amber-500 text-white',
          accent: 'text-amber-700 dark:text-amber-300',
        }
      : {
          bg: 'bg-indigo-50 dark:bg-indigo-900/10',
          ring: 'ring-indigo-400',
          chip: 'bg-indigo-600 text-white',
          accent: 'text-indigo-700 dark:text-indigo-300',
        };

  return (
    <div
      className={`rounded-2xl p-4 sm:p-6 border-2 transition-colors ${swatch.bg} border-gray-200 dark:border-gray-700`}
      data-testid="vista-segmentada"
    >
      {/* Fase 6 fix H-03 (2026-07-01): barra de navegación entre días —
          espejo del selector de la vista unificada. Se muestra solo si el
          padre pasa `dias` y `onCambiarDia`. Cada día trae su estado
          (borrador / solicitado / …) como chip para orientar al usuario. */}
      {dias && dias.length > 0 && onCambiarDia && (
        <div
          className="mb-4 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Días de la semana"
          data-testid="tabs-dias-segmentado"
        >
          {dias.map((d) => {
            const activo = d.fecha === diaActivo;
            return (
              <button
                key={d.fecha}
                type="button"
                role="tab"
                aria-selected={activo}
                onClick={() => onCambiarDia(d.fecha)}
                data-testid={`tab-dia-${d.fecha}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                  activo
                    ? 'bg-[#29ABE2] text-white shadow'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-700'
                }`}
              >
                <span className="capitalize">{d.nombre}</span>
                <span className="opacity-70 hidden sm:inline">
                  {d.fecha.slice(5)}
                </span>
                {d.estado && d.estado !== 'borrador' && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      d.estado === 'solicitado'
                        ? 'bg-emerald-500/90 text-white'
                        : 'bg-gray-600/80 text-white'
                    }`}
                  >
                    {d.estado}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Header: fecha + toggle día/noche */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Armar pedido
          </p>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 capitalize">
            {formatearFechaLarga(diaActivo)}
          </h2>
        </div>

        <div
          role="tablist"
          aria-label="Segmento del pedido"
          className="inline-flex bg-white dark:bg-gray-800 rounded-xl p-1 ring-1 ring-gray-200 dark:ring-gray-700 shadow-sm"
        >
          <button
            type="button"
            role="tab"
            aria-selected={segmento === 'dia'}
            data-testid="tab-segmento-dia"
            onClick={() => setSegmento('dia')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              segmento === 'dia'
                ? 'bg-amber-500 text-white shadow'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Sun className="w-4 h-4" />
            Día
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={segmento === 'noche'}
            data-testid="tab-segmento-noche"
            onClick={() => setSegmento('noche')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              segmento === 'noche'
                ? 'bg-indigo-600 text-white shadow'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Moon className="w-4 h-4" />
            Noche
          </button>
        </div>
      </div>

      {/* Grilla elegible del segmento */}
      {filasDelSegmento.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-60" />
          No hay platos elegibles para este segmento.
        </div>
      ) : (
        <div className="space-y-3">
          {filasDelSegmento.map((fila) => {
            const tp = fila.tipoPlato;
            const celda = fila.dias.find((d) => d.fecha === diaActivo);
            if (!celda) return null;
            const receta = celda.receta;
            const k = claveCantidadSeg(tp.id, tp.filaOrden, segmento);
            const cantidad = cantidadesDia.get(k) || 0;
            return (
              <div
                key={`${tp.id}-${tp.filaOrden}-${segmento}`}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 ring-1 ring-gray-200 dark:ring-gray-700 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                data-testid={`fila-tp-${tp.id}-${segmento}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
                      style={{
                        background: `${tp.color}22`,
                        color: tp.color,
                      }}
                    >
                      {tp.nombre}
                    </span>
                    {tp.dual && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                        día y noche
                      </span>
                    )}
                  </div>
                  {receta ? (
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {receta.nombre}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">
                      sin receta asignada en la minuta
                    </p>
                  )}
                  {receta?.descripcion && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {receta.descripcion}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor={`qty-${k}`}
                    className="text-xs text-gray-500 dark:text-gray-400"
                  >
                    Porciones
                  </label>
                  <input
                    id={`qty-${k}`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={cantidad}
                    onChange={(e) =>
                      onCantidadChange(
                        diaActivo,
                        tp.id,
                        tp.filaOrden,
                        segmento,
                        parseInt(e.target.value || '0', 10) || 0
                      )
                    }
                    disabled={!receta}
                    data-testid={`qty-${tp.id}-${tp.filaOrden}-${segmento}`}
                    className={`w-20 h-11 px-3 text-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:outline-none ${
                      segmento === 'dia'
                        ? 'focus:ring-amber-400'
                        : 'focus:ring-indigo-400'
                    } disabled:opacity-50`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sección BEBIDAS — transversal al día, siempre visible cuando la
          minuta trae filas BEBIDA. Se muestra debajo de la grilla del
          segmento activo pero comparte las cantidades entre día/noche. */}
      {filasBebidaDia.length > 0 && (
        <div
          className="mt-6 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm p-4"
          data-testid="seccion-bebidas"
        >
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <GlassWater className="w-4 h-4 text-cyan-600 dark:text-cyan-300" />
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              🥤 Bebidas
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              (transversales — aplican al día completo)
            </span>
            <span
              className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                excedeBebidas
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
              }`}
              data-testid="bebidas-total"
            >
              {totalBebidasDia} · comida {totalComidaDia}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {filasBebidaDia.map(({ fila, celda }) => {
              if (!celda?.receta) return null;
              const tp = fila.tipoPlato;
              const k = claveCantidadSeg(tp.id, tp.filaOrden, SEG_BEBIDA);
              const cantidad = cantidadesDia.get(k) || 0;
              return (
                <div
                  key={`bebida-${tp.id}-${tp.filaOrden}`}
                  className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                  data-testid={`fila-bebida-${tp.filaOrden}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {celda.receta.nombre}
                    </p>
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={cantidad}
                    onChange={(e) =>
                      onCantidadChange(
                        diaActivo,
                        tp.id,
                        tp.filaOrden,
                        // El backend acepta transversal en pedido_detalles.segmento;
                        // el prop tipa como Segmento por compat, casteamos aquí.
                        SEG_BEBIDA as unknown as Segmento,
                        parseInt(e.target.value || '0', 10) || 0
                      )
                    }
                    data-testid={`qty-bebida-${tp.filaOrden}`}
                    className="w-20 h-9 px-3 text-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:outline-none focus:ring-cyan-400"
                  />
                </div>
              );
            })}
          </div>
          {excedeBebidas && (
            <div
              className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-xs px-3 py-2 rounded"
              data-testid="bebidas-exceden-alerta"
            >
              Las bebidas ({totalBebidasDia}) no pueden superar la cantidad de
              comida ({totalComidaDia}).
            </div>
          )}
        </div>
      )}

      {/* Por-defectos: sección "Se incluyen automáticamente" */}
      {porDefectoSegmento.length > 0 && (
        <div
          className="mt-6 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-4"
          data-testid="seccion-auto-incluidos"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className={`w-4 h-4 ${swatch.accent}`} />
            <h3 className={`text-sm font-semibold ${swatch.accent}`}>
              Se incluyen automáticamente
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              (recalculado en vivo según las porciones del segmento)
            </span>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {porDefectoSegmento.map((pd) => (
              <li
                key={`${pd.recetaId}-${pd.segmento}`}
                className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg px-3 py-2 ring-1 ring-gray-200 dark:ring-gray-700"
                data-testid={`pd-${pd.recetaId}-${pd.segmento}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-gray-900 dark:text-gray-100 truncate">
                    {pd.recetaNombre}
                  </span>
                  {pd.esInsumo && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      insumo
                    </span>
                  )}
                  {pd.segmento === 'transversal' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      transversal
                    </span>
                  )}
                </div>
                <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                  {pd.cantidadCalculada}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Acción: guardar borrador (día+noche del día actual) + confirmar el
          pedido completo del día. Guardar persiste como 'borrador' con
          detalles marcados por segmento. Confirmar transiciona el pedido
          a 'solicitado' — con eso aparece en Gestión de Pedidos y luego
          Chef puede enviarlo a Cocina·Tandas. */}
      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Total del segmento{' '}
          <span className="font-semibold">{totalDelSegmento}</span> porciones
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {estadoDiaActivo === 'borrador' && (
            <button
              type="button"
              onClick={() => onGuardar(diaActivo)}
              disabled={guardando || !tieneCambios || excedeBebidas}
              title={
                excedeBebidas
                  ? 'Corrige las bebidas antes de guardar (no pueden superar la comida).'
                  : !tieneCambios
                    ? 'No hay cambios por guardar.'
                    : ''
              }
              data-testid="btn-guardar-segmentado"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#29ABE2] hover:bg-[#1f8fbf] focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
            >
              <Save className="w-4 h-4" />
              {guardando ? 'Guardando…' : 'Guardar pedido'}
            </button>
          )}
          {onConfirmar && estadoDiaActivo === 'borrador' && pedidoIdDiaActivo && (
            <button
              type="button"
              onClick={() =>
                onConfirmar(
                  diaActivo,
                  nombreDiaActivo ?? diaActivo,
                  pedidoIdDiaActivo
                )
              }
              disabled={
                tieneCambios ||
                guardando ||
                excedeBebidas ||
                (totalDelDia !== undefined && totalDelDia === 0)
              }
              title={
                totalDelDia !== undefined && totalDelDia === 0
                  ? 'Ingresa al menos 1 porción antes de confirmar.'
                  : excedeBebidas
                    ? 'Corrige las bebidas antes de confirmar (no pueden superar la comida).'
                    : tieneCambios
                      ? 'Guardá los cambios antes de confirmar'
                      : 'Enviar el pedido a Gestión de Pedidos'
              }
              data-testid="btn-confirmar-segmentado"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#F7941D] hover:bg-[#d97d12] focus:outline-none focus:ring-2 focus:ring-[#F7941D]/40"
            >
              Confirmar pedido del día
            </button>
          )}
          {estadoDiaActivo === 'solicitado' && (
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              data-testid="chip-confirmado-segmentado"
            >
              Pedido confirmado — solicitado
            </span>
          )}
          {estadoDiaActivo && estadoDiaActivo !== 'borrador' &&
            estadoDiaActivo !== 'solicitado' && (
              <span className="text-xs text-gray-500">
                Estado: {estadoDiaActivo}
              </span>
            )}
        </div>
      </div>
    </div>
  );
};

export default VistaSegmentadaDiaNoche;
